import { createFileRoute, useRouter } from "@tanstack/react-router";

import { RootErrorView } from "@/components/common/RootErrorView";
import { SharedConversationView } from "@/components/common/SharedConversationView";
import { Skeleton } from "@/components/ui/skeleton";
import { ShareTokenProvider } from "@/contexts/ShareTokenContext";
import { apiClient } from "@/lib/api";
import { AppError, isAppError } from "@/lib/errors/app-error";
import { cn } from "@/lib/utils";
import type { SharedConversation } from "@/types";

type SharedConversationLoaderData =
  | { ok: true; conversation: SharedConversation }
  | { ok: false; error: AppError };

function SharedConversationPendingView() {
  return (
    <div className="flex flex-col gap-6 p-6 max-w-5xl mx-auto w-full">
      {(["user-1", "assistant-1", "user-2", "assistant-2"] as const).map(
        (id, i) => (
          <Skeleton
            key={id}
            className={cn("h-16 rounded-2xl", i % 2 === 0 ? "w-2/3" : "w-full")}
          />
        )
      )}
    </div>
  );
}

function SharedConversationPage() {
  const { token } = Route.useParams();
  const data = Route.useLoaderData();
  const router = useRouter();

  if (!data.ok) {
    return (
      <RootErrorView
        error={data.error}
        reset={() => {
          void router.invalidate();
        }}
      />
    );
  }

  return (
    <ShareTokenProvider token={token}>
      <SharedConversationView conversation={data.conversation} />
    </ShareTokenProvider>
  );
}

export const Route = createFileRoute("/_public/share/$token")({
  component: SharedConversationPage,
  pendingComponent: SharedConversationPendingView,
  pendingMs: 0,
  pendingMinMs: 0,
  loader: async ({ params }): Promise<SharedConversationLoaderData> => {
    try {
      const conversation = await apiClient.getSharedConversation(params.token);
      return { ok: true, conversation };
    } catch (error) {
      if (isAppError(error) && error.status === 404) {
        return {
          ok: false,
          error: new AppError(error.message, {
            code: "shared_conversation_unavailable",
            status: 404,
          }),
        };
      }
      throw error;
    }
  },
});
